import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Combobox,
  ComboboxItem,
  Group,
  Input,
  Loader,
  Pill,
  PillsInput,
  ScrollArea,
  useCombobox,
} from "@mantine/core";
import { IconCheck, IconSearch } from "@tabler/icons-react";
import i18n from "src/i18n";

import { truncateWithEllipsis } from "@utils/truncateText";

import { StyledPillsInput } from "./MultiSelectWithSearch.styled";


interface MultiSelectWithSearchProps {
  label?: string;
  withAsterisk?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  data: { label: string; value: string }[];
  value: ComboboxItem[];
  setValue: (value: React.SetStateAction<ComboboxItem[]>) => void;
  maxDisplayedValues?: number;
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  required?: boolean;
  withinPortal?: boolean;
}

export const MultiSelectWithSearch = ({
  label,
  withAsterisk = false,
  placeholder = i18n.t("select-some-options"),
  isLoading = false,
  data = [],
  value,
  setValue,
  maxDisplayedValues = 2,
  searchable = true,
  searchPlaceholder = i18n.t("search"),
  disabled = false,
  required = false,
  withinPortal = false,
}: MultiSelectWithSearchProps) => {
  const { t } = useTranslation();

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  const [search, setSearch] = useState("");

  const handleValueSelect = (val: { label: string; value: string }) =>
    setValue(current =>
      current.some(obj => obj.value === val.value)
        ? current.filter(v => v.value !== val.value)
        : [...current, val]
    );

  const handleValueRemove = (val: { label: string; value: string }) =>
    setValue(current => current.filter(v => v.value !== val.value));

  const values = value
    ?.slice(
      0,
      //   maxDisplayedValues === value.length
      //     ? maxDisplayedValues
      //     : maxDisplayedValues - 1
      maxDisplayedValues - 1
    )
    .map((item, index) => (
      <Pill
        bg={"var(--clr-primary-100)"}
        c={"var(--clr-primary-700)"}
        key={index}
        withRemoveButton
        onRemove={() => handleValueRemove(item)}>
        {truncateWithEllipsis(item.label, 30)}
      </Pill>
    ));

  const options = data
    ?.filter(item =>
      item.label.toLowerCase().includes(search.trim().toLowerCase())
    )
    .map(item => (
      <Combobox.Option
        value={item.value}
        fz={13}
        key={item.value}
        active={value.some(obj => obj.value === item.value)}>
        <Group gap="sm">
          {value.some(obj => obj.value === item.value) ? (
            <IconCheck size={16} color="var(--mantine-color-black)" />
          ) : null}
          <span>{item.label}</span>
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox
      store={combobox}
      zIndex={9999}
      withinPortal={withinPortal}
      onOptionSubmit={(val, option) => {
        // console.log(option);
        const obj = {
          label: (option.children as any)?.props?.children?.at(1)?.props
            ?.children as string,
          value: val,
        };
        // console.log(obj);
        handleValueSelect(obj);
      }}>
      <Combobox.DropdownTarget>
        <StyledPillsInput
          rightSection={
            isLoading ? (
              <Loader size={16} color="accent.4" />
            ) : (
              <Combobox.Chevron />
            )
          }
          label={label}
          withAsterisk={withAsterisk}
          required={required}
          pointer
          onClick={() => combobox.toggleDropdown()}>
          <Pill.Group>
            {value.length > 0 ? (
              <>
                {values}
                {value.length >= maxDisplayedValues && (
                  <Pill
                    bg={"var(--clr-primary-100)"}
                    c={"var(--clr-primary-700)"}>
                    {t("plus-x-more", {
                      amount: value.length - (maxDisplayedValues - 1),
                    })}
                  </Pill>
                )}
              </>
            ) : (
              <Input.Placeholder c="var(--clr-gray-300)" fz={13}>
                {placeholder}
              </Input.Placeholder>
            )}

            <Combobox.EventsTarget>
              <PillsInput.Field
                required={withAsterisk}
                type="hidden"
                disabled={disabled}
                // onBlur={() => combobox.closeDropdown()}
                onKeyDown={event => {
                  if (event.key === "Backspace" && search.length === 0) {
                    event.preventDefault();
                    handleValueRemove(value[value.length - 1]);
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </StyledPillsInput>
      </Combobox.DropdownTarget>

      <Combobox.Dropdown>
        {searchable ? (
          <Combobox.Search
            data-autofocus
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            leftSectionPointerEvents="none"
          />
        ) : null}
        {options?.length ? (
          <Combobox.Options>
            <ScrollArea.Autosize mah={200} type="auto" scrollbarSize={7}>
              {options}
            </ScrollArea.Autosize>
          </Combobox.Options>
        ) : (
          <Combobox.Empty>{t("nothing-found")}</Combobox.Empty>
        )}
      </Combobox.Dropdown>
    </Combobox>
  );
};
